import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AXLE, fd } from "@/components/tyres/common";
import { api, errMsg } from "@/lib/api";

const NONE = "__none";
const linesFrom = (changes, sel) => {
  const m = {};
  changes.filter((c) => sel.includes(c.id)).forEach((c) => { m[c.new_type_id] = (m[c.new_type_id] || 0) + c.qty; });
  return Object.entries(m).map(([type_id, qty]) => ({ type_id, qty }));
};

export const OrderDialog = ({ sid, isAdmin, types, shops, onClose, onSaved }) => {
  const [changes, setChanges] = useState([]);
  const [sel, setSel] = useState([]);
  const [q, setQ] = useState("");
  const [lines, setLines] = useState([]);
  const [shop, setShop] = useState(shops.defaults[sid] || NONE);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/tyres/changes", { params: { station_id: isAdmin ? sid : undefined, unordered: true } }).then((r) => setChanges(r.data)); }, [sid, isAdmin]);
  const toggle = (id) => { const s = sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]; setSel(s); setLines(linesFrom(changes, s)); };
  const setLine = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const label = (id) => types.find((t) => t.id === id)?.label || "—";
  const valid = lines.length && lines.every((l) => l.type_id && Number(l.qty) > 0);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/tyres/orders", { station_id: isAdmin ? sid : undefined, shop_id: shop === NONE ? null : shop, change_ids: sel, notes,
        lines: lines.map((l) => ({ type_id: l.type_id, qty: Number(l.qty) })) });
      toast.success("Ordine creato"); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="tyre-order-dialog">
        <DialogHeader>
          <DialogTitle>Nuovo ordine gomme</DialogTitle>
          <DialogDescription>Seleziona i cambi gomme da riordinare: le gomme montate diventano le righe dell'ordine.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center justify-between gap-2"><Label>Cambi da riordinare ({sel.length} selezionati)</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca targa..." className="h-8 w-40" data-testid="tyre-order-search" /></div>
          <div className="max-h-56 divide-y overflow-y-auto rounded-lg border" data-testid="tyre-order-changes">
            {changes.filter((c) => !q.trim() || c.plate.toLowerCase().includes(q.trim().toLowerCase())).map((c) => (
              <label key={c.id} className="flex cursor-pointer items-center gap-3 px-3 py-2 text-xs hover:bg-slate-50">
                <input type="checkbox" checked={sel.includes(c.id)} onChange={() => toggle(c.id)} data-testid={`tyre-order-change-${c.id}`} />
                <span className="font-mono-num font-semibold">{c.plate}</span><span>{fd(c.date)}</span>
                <span className="text-slate-500">{AXLE[c.axle]}</span><span className="flex-1 truncate">{c.new_type_label}</span>{c.imported && <span className="rounded bg-slate-100 px-1.5 text-[10px] text-slate-500">storico</span>}<span className="font-semibold">×{c.qty}</span>
              </label>
            ))}
            {!changes.length && <p className="px-3 py-4 text-center text-xs text-slate-500">Nessun cambio da riordinare.</p>}
          </div>
        </div>
        <div className="space-y-2">
          <Label>Righe ordine</Label>
          {lines.map((l, i) => (
            <div key={i} className="flex gap-2" data-testid={`tyre-order-line-${i}`}>
              <Select value={l.type_id || undefined} onValueChange={(v) => setLine(i, "type_id", v)}>
                <SelectTrigger className="flex-1"><SelectValue placeholder="Gomma">{l.type_id ? label(l.type_id) : undefined}</SelectValue></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" min="1" className="w-20" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} data-testid={`tyre-order-line-qty-${i}`} />
              <Button variant="ghost" size="sm" onClick={() => setLines(lines.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setLines([...lines, { type_id: "", qty: 2 }])} data-testid="tyre-order-add-line"><Plus className="mr-1 h-4 w-4" /> Aggiungi riga</Button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>Gommista</Label>
            <Select value={shop} onValueChange={setShop}>
              <SelectTrigger data-testid="tyre-order-shop"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={NONE}>Nessuno</SelectItem>{shops.shops.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Note</Label><Textarea rows={1} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="tyre-order-notes" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Annulla</Button><Button onClick={save} disabled={busy || !valid} data-testid="tyre-order-save">{busy ? "Salvataggio..." : "Crea ordine"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
